import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Autocomplete,
  DropDown,
  DropDownOption,
  Flex,
  FlexColumn,
  FormControls,
  Switch,
  TextField,
} from '@elementary/common-ui/components';
import { isValidFileName } from '@elementary/common-ui/utils/common';
import { Tooltip, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import OwnersChipListAutocomplete from 'shared/components/OwnersChipListAutocomplete';
import TagsChipListAutocomplete from 'shared/components/TagsChipListAutocomplete';
import { AddTestForm, Resource } from 'shared/types';
import CloudTestScheduleFields from '../CloudTestScheduleFields';
import {
  CUSTOM_SQL_CONDITIONAL_OPERATORS,
  DEFAULT_CUSTOM_SQL_CONFIG,
  MAX_QUERY_TIMEOUT_SECONDS,
  MAX_SAMPLE_ROW_LIMIT,
} from '../constants';
import { clampCustomSqlBoundedNumber } from '../customSqlCloudTest';
import FieldRow from '../FieldRow';
import QualityDimensionSelector from '../QualityDimensionSelector';
import SeveritySelector from '../SeveritySelector';
import CompactChipField from './shared/CompactChipField';
import TestHeader from './shared/TestHeader';

const TestedTableTooltipTarget = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

type DefaultingNumericTextFieldProps = {
  value?: number;
  onChange: (value: number) => void;
  onBlur: () => void;
  defaultValue: number;
  min: number;
  max: number;
  disabled?: boolean;
  error?: boolean;
  'aria-label': string;
};

const DefaultingNumericTextField = ({
  value,
  onChange,
  onBlur,
  defaultValue,
  min,
  max,
  disabled,
  error,
  'aria-label': ariaLabel,
}: DefaultingNumericTextFieldProps) => {
  const [rawValue, setRawValue] = useState<string | null>(null);
  const displayValue = rawValue ?? (value == null ? '' : String(value));

  return (
    <TextField
      sx={{ width: '134px' }}
      size="large"
      numeric
      disabled={disabled}
      error={error}
      inputProps={{ 'aria-label': ariaLabel }}
      value={displayValue}
      onChange={(event) => {
        const next = event.target.value;
        setRawValue(next);
        if (next !== '') {
          onChange(Number(next));
        }
      }}
      onBlur={(event) => {
        onChange(
          clampCustomSqlBoundedNumber(
            event.target.value,
            defaultValue,
            min,
            max,
          ),
        );
        setRawValue(null);
        onBlur();
      }}
    />
  );
};

type Props = {
  methods: UseFormReturn<AddTestForm>;
  singularTestPaths: string[];
  tableResourcesData: Resource[];
  isRequiredDataAccessTest?: boolean;
};

const CustomQuery = ({
  methods,
  singularTestPaths,
  tableResourcesData,
  isRequiredDataAccessTest = false,
}: Props) => {
  const destination = methods.watch('customQueryDestination');
  const isCloudDestination = destination === 'cloud';
  const storeRowSample = methods.watch('storeRowSample');
  const referencedAssets = methods.watch('customSqlReferencedAssets') || [];
  const requiresAssetSelection = methods.watch(
    'customSqlRequiresAssetSelection',
  );
  const isTestedTableLocked =
    isCloudDestination && !methods.watch('customSqlValidated');
  const {
    customSqlPrimaryAssetId: testedTableError,
    sampleRowLimit: sampleRowLimitError,
    queryTimeoutSeconds: queryTimeoutError,
  } = methods.formState.errors;

  useEffect(() => {
    if (!isRequiredDataAccessTest) {
      return;
    }

    if (methods.getValues('customQueryDestination') == null) {
      methods.setValue('customQueryDestination', 'cloud');
    }
  }, [methods, isRequiredDataAccessTest]);

  const testedTableOptions: DropDownOption[] = isCloudDestination
    ? referencedAssets.map((asset) => ({
        value: asset.asset_id,
        label: `${asset.name} (${asset.fqn})`,
      }))
    : tableResourcesData.map((table) => ({
        value: table.id,
        label: table.source_name
          ? `${table.source_name}.${table.name}`
          : table.name,
      }));

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <TestHeader
        methods={methods}
        titleField="testName"
        titleValidate={(value) => {
          if (!value?.trim()) {
            return 'Test name is required';
          }
          if (isCloudDestination) {
            return true;
          }
          return (
            isValidFileName(value) ||
            'Please use only letters, numbers, and underscores'
          );
        }}
      />

      <FormControls.FieldsWrapper>
        <Controller
          name="severity"
          control={methods.control}
          render={({ field }) => (
            <SeveritySelector
              {...field}
              allowConditional={isCloudDestination}
              shouldAlignLabels={!isCloudDestination}
              allowedConditionalOperators={
                isCloudDestination
                  ? CUSTOM_SQL_CONDITIONAL_OPERATORS
                  : undefined
              }
            />
          )}
          defaultValue={{ type: 'error' }}
        />

        {(isCloudDestination ||
          (isRequiredDataAccessTest && destination != null)) && (
          <FlexColumn
            sx={{ gap: 3, display: isCloudDestination ? undefined : 'none' }}
          >
            <CloudTestScheduleFields methods={methods} />
            <FieldRow
              label="Store row sample:"
              info="Whether failing rows from the query are stored as a sample"
            >
              <Controller
                name="storeRowSample"
                control={methods.control}
                shouldUnregister={false}
                render={({ field }) => (
                  <Switch
                    checked={field.value ?? true}
                    onChange={(_, checked) => field.onChange(checked)}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                  />
                )}
              />
            </FieldRow>
            <FieldRow
              label="Sample row limit:"
              info="Maximum number of failing rows to store"
              hasError={!!sampleRowLimitError}
            >
              <Controller
                name="sampleRowLimit"
                control={methods.control}
                shouldUnregister={false}
                render={({ field }) => (
                  <DefaultingNumericTextField
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      methods.clearErrors('sampleRowLimit');
                    }}
                    onBlur={field.onBlur}
                    defaultValue={DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit}
                    min={1}
                    max={MAX_SAMPLE_ROW_LIMIT}
                    disabled={storeRowSample === false}
                    error={!!sampleRowLimitError}
                    aria-label="Sample row limit"
                  />
                )}
              />
            </FieldRow>
            <FieldRow
              label="Query timeout:"
              info="Maximum time the query is allowed to run"
              hasError={!!queryTimeoutError}
            >
              <Flex sx={{ alignItems: 'center', gap: 1, flex: 1 }}>
                <Controller
                  name="queryTimeoutSeconds"
                  control={methods.control}
                  shouldUnregister={false}
                  render={({ field }) => (
                    <DefaultingNumericTextField
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        methods.clearErrors('queryTimeoutSeconds');
                      }}
                      onBlur={field.onBlur}
                      defaultValue={
                        DEFAULT_CUSTOM_SQL_CONFIG.query_timeout_seconds
                      }
                      min={1}
                      max={MAX_QUERY_TIMEOUT_SECONDS}
                      error={!!queryTimeoutError}
                      aria-label="Query timeout"
                    />
                  )}
                />
                <Typography sx={{ fontSize: '14px', color: 'common.gray2' }}>
                  seconds
                </Typography>
              </Flex>
            </FieldRow>
          </FlexColumn>
        )}

        <Controller
          name="qualityDimension"
          control={methods.control}
          render={({ field }) => (
            <QualityDimensionSelector
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        {!isCloudDestination && (
          <FieldRow label="Location:">
            <Controller
              name="path"
              control={methods.control}
              defaultValue="tests"
              render={({ field }) => (
                <Autocomplete
                  sx={{ flex: 1, minWidth: 0, width: '100%' }}
                  size="large"
                  options={singularTestPaths}
                  freeSolo
                  onSelect={field.onChange}
                  {...field}
                  onChange={undefined}
                />
              )}
            />
          </FieldRow>
        )}

        <FieldRow
          label="Tested table:"
          info="This will determine which table the test will be associated with, you will see the test results paired with the selected table in the side bar and lineage"
          hasError={!!testedTableError}
        >
          {isCloudDestination ? (
            <Tooltip
              title={
                isTestedTableLocked ? 'Run query to choose a tested table' : ''
              }
            >
              <TestedTableTooltipTarget aria-label="Tested table field">
                <Controller
                  name="customSqlPrimaryAssetId"
                  control={methods.control}
                  render={({ field }) => (
                    <DropDown
                      sx={{ flex: 1, minWidth: 0, width: '100%' }}
                      size="large"
                      placeholder="Choose tested table"
                      options={testedTableOptions}
                      showSearch
                      showClear={!requiresAssetSelection}
                      value={field.value ?? ''}
                      error={!!testedTableError}
                      disabled={isTestedTableLocked}
                      onChange={(value) => {
                        field.onChange(value);
                        methods.clearErrors('customSqlPrimaryAssetId');
                      }}
                    />
                  )}
                />
              </TestedTableTooltipTarget>
            </Tooltip>
          ) : (
            <Controller
              name="testedTable"
              control={methods.control}
              render={({ field }) => (
                <DropDown
                  sx={{ flex: 1, minWidth: 0, width: '100%' }}
                  size="large"
                  placeholder="Choose tested table"
                  options={testedTableOptions}
                  showSearch
                  showClear
                  {...field}
                />
              )}
            />
          )}
        </FieldRow>

        <FieldRow label="Owners:">
          <CompactChipField>
            <Controller
              name="owners"
              control={methods.control}
              shouldUnregister={false}
              render={({ field }) => <OwnersChipListAutocomplete {...field} />}
            />
          </CompactChipField>
        </FieldRow>
        <FieldRow label="Tags:">
          <CompactChipField>
            <Controller
              name="tags"
              control={methods.control}
              shouldUnregister={false}
              render={({ field }) => <TagsChipListAutocomplete {...field} />}
            />
          </CompactChipField>
        </FieldRow>
      </FormControls.FieldsWrapper>
    </FlexColumn>
  );
};

export default CustomQuery;
